import { useEffect, useMemo, useRef } from 'react';

import { normalizeAnalysis } from './analysis.js';
import { Playhead, useElementSize, useTimeView, useViewGestures, type AudioViewProps } from './view-hooks.js';
import './sound-editors.css';

/** Nice tick step (seconds) for a visible span. */
export function tickStep(span: number, pixels: number): number {
  const target = (span / Math.max(1, pixels)) * 90;
  const steps = [0.001, 0.002, 0.005, 0.01, 0.02, 0.05, 0.1, 0.2, 0.5, 1, 2, 5, 10, 15, 30, 60, 120];
  return steps.find((s) => s >= target) ?? 300;
}

export function formatTime(t: number, step: number): string {
  const digits = step < 0.01 ? 3 : step < 0.1 ? 2 : step < 1 ? 1 : 0;
  if (t >= 60) {
    const m = Math.floor(t / 60);
    return `${m}:${(t - m * 60).toFixed(digits).padStart(digits ? digits + 3 : 2, '0')}`;
  }
  return `${t.toFixed(digits)}s`;
}

/**
 * Waveform of a rendered buffer (stereo lanes, min/max per pixel), with a
 * time ruler, onset markers and the playhead. Wheel zooms, Shift+wheel or
 * drag scrolls, click seeks; pass `view`/`onViewChange` to sync with the
 * timeline and the spectrogram.
 */
export function WaveformView({ left, right, rate = 48000, view, onViewChange, playhead, onSeek, analysis, height = 120, className = '' }: AudioViewProps) {
  const host = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const { width, dpr } = useElementSize(host);
  const duration = left ? left.length / rate : 0;
  const [v, setView] = useTimeView(duration, view, onViewChange);
  const gestures = useViewGestures(v, setView, duration, width, onSeek);
  const onsets = useMemo(() => normalizeAnalysis(analysis)?.onsets ?? [], [analysis]);

  useEffect(() => {
    const c = canvas.current;
    if (!c || !width) return;
    c.width = Math.round(width * dpr);
    c.height = Math.round(height * dpr);
    const g = c.getContext('2d');
    if (!g) return;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    const css = getComputedStyle(c);
    const col = (name: string, fb: string) => css.getPropertyValue(name).trim() || fb;
    g.fillStyle = col('--viewport', '#101114');
    g.fillRect(0, 0, width, height);
    if (!left || !left.length) return;

    const ruler = 14;
    const chans = right && right.length ? [left, right] : [left];
    const laneH = (height - ruler) / chans.length;
    const span = v.end - v.start;
    const spp = (span * rate) / width; // samples per pixel

    chans.forEach((data, ci) => {
      const top = ruler + ci * laneH;
      const mid = top + laneH / 2;
      const amp = laneH / 2 - 2;
      g.strokeStyle = col('--border-soft', '#202227');
      g.beginPath();
      g.moveTo(0, mid + 0.5);
      g.lineTo(width, mid + 0.5);
      g.stroke();
      g.fillStyle = col('--accent', '#5b8cff');
      g.strokeStyle = col('--accent', '#5b8cff');
      if (spp > 1) {
        for (let x = 0; x < width; x++) {
          const s0 = Math.floor(v.start * rate + x * spp);
          const s1 = Math.min(data.length, Math.floor(v.start * rate + (x + 1) * spp));
          if (s0 >= data.length) break;
          let lo = 1, hi = -1;
          for (let i = Math.max(0, s0); i < Math.max(s0 + 1, s1); i++) {
            const s = data[i] ?? 0;
            if (s < lo) lo = s;
            if (s > hi) hi = s;
          }
          const y0 = mid - Math.min(1, hi) * amp;
          const y1 = mid - Math.max(-1, lo) * amp;
          g.fillRect(x, y0, 1, Math.max(1, y1 - y0));
        }
      } else {
        // Zoomed past one sample per pixel: draw the samples as a line with dots.
        g.beginPath();
        const first = Math.max(0, Math.floor(v.start * rate));
        const last = Math.min(data.length - 1, Math.ceil(v.end * rate));
        for (let i = first; i <= last; i++) {
          const x = ((i / rate - v.start) / span) * width;
          const y = mid - Math.max(-1, Math.min(1, data[i] ?? 0)) * amp;
          if (i === first) g.moveTo(x, y);
          else g.lineTo(x, y);
          if (spp < 0.15) g.fillRect(x - 1.5, y - 1.5, 3, 3);
        }
        g.stroke();
      }
      // Clipping markers.
      g.fillStyle = col('--danger', '#f87171');
      if (spp <= 4096) {
        const first = Math.max(0, Math.floor(v.start * rate));
        const last = Math.min(data.length, Math.ceil(v.end * rate));
        for (let i = first; i < last; i++) if (Math.abs(data[i] ?? 0) >= 0.9999) g.fillRect(((i / rate - v.start) / span) * width, top, 1, 3);
      }
    });

    // Ruler.
    g.fillStyle = col('--panel', '#15161a');
    g.fillRect(0, 0, width, ruler);
    const step = tickStep(span, width);
    g.fillStyle = col('--muted', '#868b96');
    g.strokeStyle = col('--border', '#2a2d34');
    g.font = '10px system-ui, sans-serif';
    for (let t = Math.ceil(v.start / step) * step; t <= v.end; t += step) {
      const x = Math.round(((t - v.start) / span) * width) + 0.5;
      g.beginPath();
      g.moveTo(x, ruler - 4);
      g.lineTo(x, height);
      g.globalAlpha = 0.35;
      g.stroke();
      g.globalAlpha = 1;
      g.fillText(formatTime(t, step), x + 3, 10);
    }

    // Onsets.
    g.strokeStyle = col('--warn', '#fbbf24');
    g.globalAlpha = 0.8;
    for (const t of onsets) {
      if (t < v.start || t > v.end) continue;
      const x = Math.round(((t - v.start) / span) * width) + 0.5;
      g.beginPath();
      g.setLineDash([3, 3]);
      g.moveTo(x, ruler);
      g.lineTo(x, height);
      g.stroke();
    }
    g.setLineDash([]);
    g.globalAlpha = 1;

  }, [left, right, rate, v.start, v.end, width, dpr, height, onsets]);

  return (
    <div ref={host} className={`snd-view snd-waveform ${className}`} style={{ height }} {...gestures}>
      <canvas ref={canvas} style={{ width: '100%', height }} aria-label="Waveform" />
      <Playhead t={playhead} view={v} />
      {!left && <span className="snd-view-empty">Render to see the waveform.</span>}
    </div>
  );
}
